import {
  useState,
  useRef,
  useEffect,
  memo,
  forwardRef,
  useImperativeHandle
} from 'react';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import Search from '@icon/Search';
import Close from '@icon/CloseThick';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { noOp } from '@constants/empty';

const SearchTextField = styled(OutlinedInput)`
  height: 100%;
  &.MuiInputBase-root {
    width: 100%;
    justify-content: flex-end;
    transition: width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    height: 48px;
    cursor: pointer;
    border-radius: ${Spacings.SPACING_6};
    &.Mui-focused {
      width: 100%;
    }
  }
  &.MuiOutlinedInput-root .MuiInputBase-input {
    ::placeholder {
      color: var(--color-text-primary);
      opacity: 1;
      font-weight: ${Fonts.SEMIBOLD};
      font-size: ${Fonts.H6};
      line-height: ${Fonts.HEADER_LINE_HEIGHT};
    }
    cursor: pointer;
    text-shadow: ${({ $currentSelectedItem }) =>
      $currentSelectedItem !== -1 && '0 0 0 var(--color-text-primary)'};
    color: ${({ $currentSelectedItem }) =>
      $currentSelectedItem !== -1
        ? 'transparent'
        : 'var(--color-text-primary)'};
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.H6};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
    padding-right: 0;
  }
  &.Mui-focused .MuiInputBase-input {
    width: 100%;
    padding-right: ${Spacings.SPACING_14};
    ::placeholder {
      color: var(--color-text-secondary);
    }
  }
  &.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    top: -5px;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0;
    padding: 0;
    position: absolute;
    transition: padding-left 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    border-radius: inherit;
    pointer-events: none;
    border: ${({ $showBorder }) =>
      $showBorder ? '1px solid var(--color-divider);' : 'none;'};
  }
  &:hover.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--color-divider);
    border-width: 1px;
    border-radius: ${Spacings.SPACING_2B};
  }
`;

const MainWrapper = styled.div`
  width: 100%;
  align-self: center;
  position: relative;
  ${mediaQuery.uptoTablet} {
    grid-column: 10/17;
  }
`;

const SearchIconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: -${Spacings.SPACING_2B};
  background-color: transparent;
  transition: background-color 300ms ease-in-out;
`;

const SearchIcon = () => (
  <InputAdornment position="start">
    <SearchIconContainer>
      <Search size="24px" />
    </SearchIconContainer>
  </InputAdornment>
);

const CloseIcon = ({ onClick }) => (
  <InputAdornment position="end">
    <SearchIconContainer>
      <Close onClick={onClick} />
    </SearchIconContainer>
  </InputAdornment>
);

const SearchTab = (
  {
    fullWidth,
    placeholder,
    options,
    selectedOption,
    onChange: changeHandler,
    onSelect,
    onFocus,
    onBlur,
    keyHandle,
    dropdownOptionProps,
    DropdownOptionList
  },
  ref
) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const inputRef = useRef();
  const [searchText, setSearchText] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = useState(-1);
  const query = selectedText || searchText;
  const isFullWidth = fullWidth || !!query;

  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText('');
      setSelectedText('');
      changeHandler('');
    },
    focus: () => {
      inputRef.current.focus();
    },
    blur: () => {
      inputRef.current.blur();
    }
  }));

  const hideDropdown = () => {
    setShowDropdown(false);
    onBlur();
  };
  useEffect(() => {
    document.addEventListener('click', hideDropdown);

    return () => {
      document.removeEventListener('click', hideDropdown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setSearchText(e.target.value);
    setCurrentSelectedItem(-1);
    setSelectedText('');
    changeHandler(e.target.value);
  };
  const handleRemove = (e) => {
    // on clicking on cross icon.
    e.preventDefault();
    setSearchText('');
    setSelectedText('');
    inputRef.current.focus();
    changeHandler('');
    if (onSelect)
      onSelect({
        optionExists: false,
        selectedOption: ''
      });
  };
  const handleFocus = () => {
    inputRef.current.focus();
    setShowDropdown(true);
    onFocus();
  };
  const keyHandler = (e) => {
    let newIndex = currentSelectedItem;
    const data = options;
    // highlighting option item
    if (e.keyCode === 40 && newIndex + 1 < data.length) newIndex += 1;
    else if (e.keyCode === 38 && newIndex >= 0) newIndex -= 1;
    if ((e.keyCode === 40 || e.keyCode === 38) && data?.[newIndex]) {
      setSelectedText(data[newIndex].query || data[newIndex].label);
    }
    // on hitting enter
    else if (e.keyCode === 13) {
      onSelect({
        optionExists: newIndex >= 0,
        selectedOption: data?.[newIndex] || e.target.value
      });
      setSearchText(data?.[newIndex] ? data[newIndex].label : e.target.value);
      hideDropdown();
      inputRef.current.blur();
    }
    setCurrentSelectedItem(newIndex);
  };

  const handleOptionClick = (option) => {
    if (onSelect) onSelect({ optionExists: true, selectedOption: option });
    setSearchText(option.label);
    setSelectedText(option.label);
    hideDropdown();
  };

  const handleStopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <MainWrapper
      id="searchTab"
      isLoggedIn={isLoggedIn}
      onClick={handleStopPropagation}
    >
      <SearchTextField
        placeholder={placeholder}
        startAdornment={<SearchIcon />}
        fullWidth={isFullWidth}
        onChange={handleChange}
        value={query}
        endAdornment={!!query && <CloseIcon onClick={handleRemove} />}
        inputRef={inputRef}
        onKeyDown={keyHandle ? keyHandler : noOp}
        onFocus={handleFocus}
        id="searchInput"
        $currentSelectedItem={currentSelectedItem}
        autoComplete="off"
        className="search-dd-input-wrapper"
      />
      {showDropdown && (
        <DropdownOptionList
          options={options}
          dropdownOptionProps={dropdownOptionProps}
          currentSelectedItem={currentSelectedItem}
          handleOptionClick={handleOptionClick}
          selectedOption={selectedOption}
        />
      )}
    </MainWrapper>
  );
};

SearchTab.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  fullWidth: PropTypes.bool,
  keyHandle: PropTypes.bool,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired
    })
  ).isRequired
};

SearchTab.defaultProps = {
  onChange: () => {},
  onSelect: () => {},
  fullWidth: true,
  placeholder: 'Search',
  keyHandle: false
};

export default memo(forwardRef(SearchTab));

CloseIcon.propTypes = {
  onClick: PropTypes.func.isRequired
};
